import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tabs } from 'antd';
import moment from 'moment';
import ProfileTextWithLabel from '../../UI/ProfileTextWithLabel/ProfileTextWithLabel';
import { PERSONAL_TRAINING_SESSION_DETAILS, CLASS_DETAILS, INDUCTION_CLASS_DETAILS } from '../../../constants';
import { returnProductTypeName } from '../../../shared/classFunctions';
import Loading from '../../../components/Loading/Loading';
import './ClientProfileProductsDetail.scss';
import { returnAvailableClassTypesInGym } from '../../../shared/gymFunctions';
import { isMembershipUser } from '../../../shared/userFunctions';
import useTabIndex from '../../../hooks/useTabIndex';
import { useNavigate } from 'react-router-dom';
import { classNames } from '../../../shared/utility';
import { getVideoProgressOfUser } from '../../../apiFunctions/apiFunctions';
import Thumbnail from '../../../containers/ManageVideos/ShowVideos/Thumbnail/Thumbnail';
import ExportToCSV from '../../../containers/AdminDashboard/ExportToCSV/ExportToCSV';

const ClientProfileProductsDetail = ({ gymSettings, selectedClient, bookings, gymId }) => {
  const [products, setProducts] = useState([]);
  const [futureBookings, setFutureBookings] = useState([]);
  const [pastBookings, setPastBookings] = useState([]);
  const { activeTabIndex, secondActiveTabIndex, onTabChange } = useTabIndex('clientProfileProductDetailTabs');
  const allTabs = returnAvailableClassTypesInGym(gymSettings);
  const navigate = useNavigate();

  const { data: usersVideoProgress, userVideoProgressLoading } = useQuery({
    queryKey: ['userVideoProgress', selectedClient._id],
    queryFn: () => getVideoProgressOfUser(selectedClient._id),
    enabled: !!selectedClient._id
  });

  const clientNameForExport = `${selectedClient.name || ''} ${selectedClient.last_name || ''}`
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const clientBookingsExportFileName = `${clientNameForExport || 'client'}-bookings-export-${moment().format(
    'DDMMYYYY'
  )}.csv`;
  useEffect(
    () => {
      setProducts(
        selectedClient.products.filter((product) => product.productType === allTabs[activeTabIndex].productType)
      );
      setFutureBookings(
        bookings.filter(
          (booking) =>
            booking.class &&
            booking.class.__t === allTabs[activeTabIndex].type &&
            moment() <
              moment(booking?.class?.classDate).set({
                hour: moment(booking.class.classTime, ['hh:mm A']).get('hour'),
                minute: moment(booking.class.classTime, ['hh:mm A']).get('minute')
              })
        )
      );
      setPastBookings(
        bookings.filter(
          (booking) =>
            booking.class &&
            booking.class.__t === allTabs[activeTabIndex].type &&
            moment() >
              moment(booking?.class?.classDate).set({
                hour: moment(booking.class.classTime, ['hh:mm A']).get('hour'),
                minute: moment(booking.class.classTime, ['hh:mm A']).get('minute')
              })
        )
      );
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTabIndex]
  );

  if (userVideoProgressLoading) {
    return <Loading />;
  }

  const getBookingList = (bookings) =>
    bookings.map((booking) => (
      <div
        className="booking-item"
        onClick={() => {
          navigate(`/gym/${gymId}/class/${booking.class._id}`);
        }}
        key={booking._id}>
        <ProfileTextWithLabel
          key={booking._id}
          label={`Status -  ${booking.status}`}
          text={`${moment(booking.classDate).format('Do MMM YYYY')}`}
        />
      </div>
    ));
  const returnTabChildren = (type, usersClassesLeft) => (
    <>
      {type === PERSONAL_TRAINING_SESSION_DETAILS.type ? (
        <ProfileTextWithLabel
          label="Remaining:"
          text={
            selectedClient[usersClassesLeft] === 'error'
              ? 'error'
              : selectedClient[usersClassesLeft] === 0
                ? 0
                : selectedClient[usersClassesLeft].map((elem, i) => (
                    <span key={i}>
                      {elem.classesLeft} x {elem.duration} mins {elem.places > 1 && `(${elem.places} places)`}
                    </span>
                  ))
          }
        />
      ) : type === CLASS_DETAILS.type && isMembershipUser(selectedClient) ? (
        <>
          <ProfileTextWithLabel
            label="Remaining this month:"
            text={selectedClient.classesLeft === true ? 'Unlimited' : selectedClient.classesLeft}
          />
          <ProfileTextWithLabel
            label="Remaining next month:"
            text={selectedClient.classesLeftNextMonth === true ? 'Unlimited' : selectedClient.classesLeftNextMonth}
          />
        </>
      ) : (
        <ProfileTextWithLabel label="Remaining:" text={selectedClient[usersClassesLeft]} />
      )}
      <Tabs
        activeKey={secondActiveTabIndex.toString()} // controlled nested tab
        onChange={(key) => onTabChange(activeTabIndex, Number(key))}
        items={[
          {
            key: '0',
            label: 'Pass Purchases',
            children:
              products.length > 0
                ? products.map((elem) => (
                    <ProfileTextWithLabel
                      key={elem._id}
                      label={returnProductTypeName(elem.productType)}
                      text={`${elem.classCount}x, bought on ${moment(elem.orderDate).format('Do MMM YY h:mm A')}`}
                    />
                  ))
                : 'No details available'
          },
          {
            key: '1',
            label: 'Past Bookings',
            children: (
              <>
                {pastBookings.length === 0 && 'No past bookings available'}
                {getBookingList(pastBookings)}
              </>
            )
          },
          {
            key: '2',
            label: 'Future Bookings',
            children: (
              <>
                {futureBookings.length === 0 && 'No future bookings'}
                {getBookingList(futureBookings)}
              </>
            )
          },
          {
            key: '3',
            label: 'Video Purchases',
            children:
              type === INDUCTION_CLASS_DETAILS.type ? (
                usersVideoProgress?.purchasedVideoPass?.length ? (
                  <div key="pass-name" style={{ marginBottom: '5%' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <ProfileTextWithLabel
                        label={usersVideoProgress.purchasedVideoPass[0]?.productId?.name || ''}
                        text={`Bought on ${moment(usersVideoProgress.purchasedVideoPass[0]?.orderDate).format(
                          'Do MMM YY h:mm A'
                        )}`}
                      />
                    </div>
                    <div key="video-progress" className="flex justify-start flex-wrap">
                      {usersVideoProgress.watchedVideoProgress?.map((elem) => (
                        <Thumbnail
                          key={elem._id}
                          video={elem.video}
                          isAdmin
                          gymId={gymId}
                          isFromClientProfile
                          percentageWatched={elem.progress}
                          showProgress
                        />
                      ))}
                      {usersVideoProgress.unwatchedVideos?.map((elem) => (
                        <Thumbnail
                          key={elem._id}
                          video={elem}
                          isAdmin
                          gymId={gymId}
                          isFromClientProfile
                          percentageWatched={elem.progress}
                          showProgress
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  'No details available'
                )
              ) : (
                'No details available'
              )
          }
        ]}
      />
    </>
  );

  const tabsItems = allTabs.map(({ title, type, usersClassesLeft }, index) => {
    return {
      key: index.toString(),
      label: title,
      children: returnTabChildren(type, usersClassesLeft)
    };
  });
  return (
    <div className={classNames('product-detail-container', 'mb-2')}>
      <div className="mt-2 mb-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <h3 className="uppercase">PURCHASES AND BOOKINGS</h3>
        <ExportToCSV
          data={bookings}
          type="clientBookingsExport"
          disabled={bookings.filter((booking) => booking.class).length === 0}
          filename={clientBookingsExportFileName}
          className="bg-secondary border-2 border-transparent hover:border-secondary hover:opacity-90"
        />
      </div>
      <Tabs
        activeKey={activeTabIndex.toString()} // controlled main tab
        onChange={(key) => onTabChange(Number(key))}
        items={tabsItems}
      />
    </div>
  );
};

export default ClientProfileProductsDetail;
